import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, ArrowLeft, Send } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { motion } from 'framer-motion'
import Logo from '../common/Logo'

const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido')
})

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>

interface ForgotPasswordFormProps {
  onBackToLogin: () => void
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const { resetPassword } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema)
  })

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true)
    try {
      await resetPassword(data.email)
      setEmailSent(true)
    } catch (error) {
      // Error is handled by the auth context
    } finally {
      setIsLoading(false)
    }
  }

  if (emailSent) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="w-full max-w-md mx-auto text-center"
      >
        <div className="w-16 h-16 bg-moss-100 dark:bg-moss-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
          <Mail className="w-8 h-8 text-moss-500" />
        </div>
        
        <h1 className="text-2xl font-bold text-ink-900 dark:text-cream-50 mb-4">Email enviado!</h1>

        <p className="text-ink-500 dark:text-ink-300 mb-6">
          Enviamos um link de recuperação para{' '}
          <span className="font-medium text-ink-900 dark:text-cream-50">{getValues('email')}</span>.
          Verifique sua caixa de entrada e spam.
        </p>
        
        <button
          onClick={onBackToLogin}
          className="w-full bg-terra-500 hover:bg-terra-700 text-white py-3 px-4 rounded-caderno-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar ao login</span>
        </button>
      </motion.div>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full max-w-md mx-auto"
    >
      <div className="text-center mb-8">
        <Logo variant="lockup-vertical" size={64} className="mb-4" />
        <h1 className="text-2xl font-bold text-ink-900 dark:text-cream-50 mb-2">Esqueceu a senha?</h1>
        <p className="text-ink-500 dark:text-ink-300">
          Digite seu email e enviaremos um link para redefinir sua senha
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-ink-700 dark:text-cream-300 mb-2">
            Email
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400 w-5 h-5" />
            <input
              {...register('email')}
              type="email"
              id="email"
              className={`w-full pl-10 pr-4 py-3 bg-surface dark:bg-ink-700 border rounded-caderno-sm focus:ring-2 focus:ring-terra-500 focus:border-transparent transition-colors text-ink-900 dark:text-cream-50 ${
                errors.email ? 'border-blood-500 dark:border-blood-500' : 'border-cream-400 dark:border-ink-500'
              }`}
              placeholder="seu@email.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-blood-500">{errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-terra-500 hover:bg-terra-700 disabled:bg-terra-300 text-white py-3 px-4 rounded-caderno-sm font-medium transition-colors flex items-center justify-center space-x-2"
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Send className="w-5 h-5" />
              <span>Enviar link de recuperação</span>
            </>
          )}
        </button>
      </form>

      <div className="mt-6 text-center">
        <button
          onClick={onBackToLogin}
          className="text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-300 font-medium flex items-center justify-center space-x-2 mx-auto"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Voltar ao login</span>
        </button>
      </div>
    </motion.div>
  )
}

export default ForgotPasswordForm